<template>
  <div
    v-if="show"
    class="fixed z-10 inset-0 bg-gray-900 bg-opacity-60 overflow-y-scroll flex items-center justify-center text-base"
  >
    <div class="bg-white rounded-lg shadow-xl w-1/2 h-[55vh] overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <h4 class="text-2xl text-gray-800 font-bold">Pay FFS Coverage</h4>
        <button
          type="button"
          class="ml-auto -mx-1.5 -my-1.5 p-1.5 inline-flex text-gray-500 items-center justify-end h-8 w-8 hover:text-yellow-500"
          @click="handleCloseModal"
          aria-label="Close"
        >
          <span class="sr-only">Dismiss</span>
          <X :size="24" />
        </button>
      </div>

      <!-- Body -->
      <div class="p-6 space-y-6">
        <!-- Patient / Visit Info (read-only) -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label class="block text-base font-medium text-gray-700">Patient Name</label>
            <p class="mt-1 text-base font-semibold text-gray-700">{{ patient.fullName }}</p>
          </div>
          <div>
            <label class="block text-base font-medium text-gray-700">ID Number</label>
            <p class="mt-1 text-base font-semibold text-gray-700">{{ patient.idNumber }}</p>
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">Visit Code</label>
            <p class="mt-1 text-base font-semibold text-gray-700">{{ patient.visit_code }}</p>
          </div>
          <div>
            <label class="block text-base font-medium text-gray-700">Visit Date</label>
            <p class="mt-1 text-base font-semibold text-gray-700">
              {{ formatDate(patient.visit_start_time) }}
            </p>
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">Beneficiary</label>
            <p class="mt-1 text-base font-semibold text-gray-700">
              {{ patient.beneficiary || 'Unknown' }}
            </p>
          </div>
          <div>
            <label class="block text-base font-medium text-gray-700">Company</label>
            <p class="mt-1 text-base font-semibold text-gray-700">
              {{ patient.company || 'Unknown' }}
            </p>
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">Total Cover</label>
            <p class="mt-1 text-base font-semibold text-gray-700">KES {{ patient.total_amount }}</p>
          </div>
          <div>
            <label class="block text-base font-medium text-gray-700">Amount Paid</label>
            <p class="mt-1 text-base font-semibold text-gray-700">KES {{ patient.amount_paid }}</p>
          </div>

          <div>
            <label class="block text-base font-medium text-gray-700">Balance</label>
            <p class="mt-1 text-base font-semibold font-semibold text-red-600">
              KES {{ patient.balance }}
            </p>
          </div>
        </div>

        <!-- Amount to Pay (editable) -->
        <div class="mt-6">
          <label for="payAmount" class="block text-sm font-medium text-gray-700">
            Amount to Pay <span class="text-red-500">*</span>
          </label>
          <input
            type="number"
            id="payAmount"
            v-model.number="payAmount"
            placeholder="Enter amount..."
            step="0.01"
            min="0"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring focus:ring-yellow-500 focus:ring-opacity-50 bg-gray-50 p-2"
          />
          <span class="text-sm text-red-500" v-if="errors.payAmount">{{ errors.payAmount }}</span>
        </div>
      </div>

      <!-- Footer -->
      <div class="flex justify-end p-4 border-t gap-3">
        <button
          type="button"
          @click="handleCloseModal"
          class="px-4 py-2 bg-gray-300 text-gray-800 rounded-md hover:bg-gray-400"
        >
          Cancel
        </button>
        <button
          type="button"
          @click="handleSubmit"
          :disabled="showLoader"
          class="px-4 py-2 bg-yellow-500 text-white rounded-md hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 flex items-center"
        >
          <span v-if="!showLoader">Pay</span>
          <span v-else class="flex items-center">
            <svg class="animate-spin h-5 w-5 text-white mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" fill="none"/>
            </svg>
            Paying…
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineProps, defineEmits } from 'vue'
import { X } from 'lucide-vue-next'
import apiClient from '@/services/api'
import { useToast } from 'vue-toast-notification'

const toast = useToast({ position: 'top-right', duration: 3000 })

const props = defineProps({
  show: { type: Boolean, required: true },
  patient: { type: Object, required: true }
})

const emits = defineEmits(['close', 'updated'])

const payAmount = ref(0);
const showLoader = ref(false);
const errors = ref({});

watch(() => props.show, (newVal) => {
  if (newVal) {
    payAmount.value = 0;
    errors.value = {};
  }
}, { immediate: true });

const formatDate = (isoString) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const validate = () => {
  errors.value = {};
  let ok = true;

  if (!payAmount.value || payAmount.value <= 0) {
    errors.value.payAmount = 'Amount must be greater that 0.';
    ok = false;
  }

  if (payAmount.value > props.patient.balance) {
    errors.value.payAmount = `Amount cannot exceed balance (KES ${props.patient.balance})`;
    ok = false;
  }

  return ok;
};

const handleSubmit = async () => {
  if (!validate()) {
    toast.error('Please fix the errors below.')
    return;
  }

  try {
    showLoader.value = true;

    const payload = {
      bill_id: props.patient.bill_id,
      paid_amount: payAmount.value
    }

    const response = await apiClient.updateFFSCoverage(props.patient.patient_id, payload);

    if (response.success) {
      toast.success(response.message);
      emits('updated');
      emits('close');
    } else {
      toast.error(response.message);
    }

  } catch (e) {
    toast.error(e.message || 'Failed to update payment');
    console.log('error obj', e);
  } finally {
    showLoader.value = false;
  }
}

const handleCloseModal = () => {
  emits('close');
};
</script>
